import { useEffect, useState, useRef, useCallback } from 'react';
import { useCanvasStore } from '../../stores/canvasStore';

const PREVIEW_SIZE = 100;
const MAGNIFICATION = 6;
const CAPTURE_SIZE = Math.ceil(PREVIEW_SIZE / MAGNIFICATION);

interface EyedropperPreviewProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

export default function EyedropperPreview({ containerRef }: EyedropperPreviewProps) {
  const activeTool = useCanvasStore((s) => s.activeTool);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [color, setColor] = useState('#000000');
  const [visible, setVisible] = useState(false);
  const magnifierCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPos({ x, y });
    setVisible(true);

    const fabricCanvas = useCanvasStore.getState().fabricCanvas;
    if (!fabricCanvas) return;

    const canvasEl = (fabricCanvas as any).lowerCanvasEl as HTMLCanvasElement | undefined;
    if (!canvasEl) return;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    const scaleX = canvasEl.width / canvasEl.clientWidth;
    const scaleY = canvasEl.height / canvasEl.clientHeight;
    const pixelX = Math.round(x * scaleX);
    const pixelY = Math.round(y * scaleY);

    const pixel = ctx.getImageData(pixelX, pixelY, 1, 1).data;
    const hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
    setColor(hex);

    const magCanvas = magnifierCanvasRef.current;
    if (magCanvas) {
      const magCtx = magCanvas.getContext('2d');
      if (magCtx) {
        magCtx.imageSmoothingEnabled = false;
        const halfCapture = Math.floor(CAPTURE_SIZE / 2);
        const srcX = pixelX - halfCapture;
        const srcY = pixelY - halfCapture;
        magCtx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
        magCtx.drawImage(
          canvasEl,
          srcX, srcY, CAPTURE_SIZE, CAPTURE_SIZE,
          0, 0, PREVIEW_SIZE, PREVIEW_SIZE
        );

        magCtx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        magCtx.lineWidth = 2;
        const center = PREVIEW_SIZE / 2;
        const cellSize = MAGNIFICATION;
        magCtx.strokeRect(center - cellSize / 2, center - cellSize / 2, cellSize, cellSize);
      }
    }
  }, [containerRef]);

  const handleMouseLeave = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (activeTool !== 'eyedropper' || !container) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [activeTool, containerRef, handleMouseMove, handleMouseLeave]);

  if (activeTool !== 'eyedropper' || !visible) return null;

  const offsetX = pos.x + 20 + PREVIEW_SIZE > (containerRef.current?.clientWidth ?? 0) ? -PREVIEW_SIZE - 20 : 20;
  const offsetY = pos.y + 20 + PREVIEW_SIZE + 32 > (containerRef.current?.clientHeight ?? 0) ? -PREVIEW_SIZE - 52 : 20;

  return (
    <div
      className="absolute pointer-events-none z-50"
      style={{
        left: pos.x + offsetX,
        top: pos.y + offsetY,
      }}
    >
      <div className="rounded-xl overflow-hidden border-2 border-white/30 shadow-2xl">
        <canvas
          ref={magnifierCanvasRef}
          width={PREVIEW_SIZE}
          height={PREVIEW_SIZE}
          className="block bg-black"
        />
      </div>
      <div
        className="mt-1.5 flex items-center gap-2 px-2 py-1 rounded-lg bg-black/80 backdrop-blur-sm border border-white/10"
      >
        <div
          className="w-4 h-4 rounded border border-white/20 flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="text-[11px] font-mono text-white/80">{color.toUpperCase()}</span>
      </div>
    </div>
  );
}
